import { useMemo } from 'react'
import { useCarriers } from '@/hooks/use-settings'
import { usePhenotypes } from '@/hooks/use-phenotypes'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Settings2 } from 'lucide-react'

export function CarrierManagement() {
  const { data: carriers, isLoading: carriersLoading } = useCarriers()
  const { data: phenotypes } = usePhenotypes()

  // Group phenotypes by carrier to get counts
  const carrierPhenotypeCount = useMemo(() => {
    if (!phenotypes) return new Map<string, number>()

    const counts = new Map<string, number>()
    phenotypes.forEach(phenotype => {
      const carrier = phenotype.carrierId
      counts.set(carrier, (counts.get(carrier) || 0) + 1)
    })
    return counts
  }, [phenotypes])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Carriers</CardTitle>
      </CardHeader>
      <CardContent>
        {carriersLoading ? (
          <div className="space-y-3">
            <div className="h-12 bg-muted animate-pulse rounded" />
            <div className="h-12 bg-muted animate-pulse rounded" />
            <div className="h-12 bg-muted animate-pulse rounded" />
          </div>
        ) : carriers && carriers.length > 0 ? (
          <div className="space-y-2">
            {carriers.map((carrier) => (
              <div
                key={carrier}
                className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <span className="font-medium">{carrier}</span>
                  <Badge variant="outline">
                    {carrierPhenotypeCount.get(carrier) || 0} phenotype(s)
                  </Badge>
                  <Badge variant="outline">Active</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled
                  title="Carrier settings (coming soon)"
                >
                  <Settings2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">
            No carriers configured
          </p>
        )}
      </CardContent>
    </Card>
  )
}
